'use client';

import { ICartGroup, ICartItem, ITotalData } from '@/api/types/cart';
import Button from '@/components/Button';
import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { EPlatform } from '@/utils/enums/platform-enum';
import { setSession } from '@/utils/tools';
import { CollapseArrowDownOutlined, LeftArrowOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { CartBottomBar } from './CartBottomBar';
import { CartEmpty } from './CartEmpty';
import { CartItem } from './cartItem/CartItem';
import InvalidCartItem from './InvalidCartItem/InvalidCartItem';
import NoticeBar from './NoticeBar';

interface CartPageWAPProps {
  // 购物车数据
  cartCanBuyList: ICartGroup[];
  cartCanNotBuyList: ICartItem[];

  // 状态
  allCheck: boolean;
  batchFlag: boolean;
  batchDisabled: boolean;
  checkoutLoading: boolean;
  showMoreInvalid: boolean;

  // 计算数据
  totalData: ITotalData;
  allQty: number;
  selectedCanBuy: ICartItem[];

  // 配置数据
  currency: any;
  halfPriceConfig: any;
  maxSaving: string;

  // 广告和推荐数据
  showAdsImg: boolean;
  advertObj: any;
  advertData: any;
  recommendList: any;
  freeShipData: any;

  // 事件回调
  onGoBack: () => void;
  onCheckChange: () => void;
  onBatchFlagChange: (flag: boolean) => void;
  onSelectChange: (index: number, checked: boolean) => void;
  onReloadCart: (cartData?: any, modifiedCartId?: number) => Promise<void>;
  onBatchCancel: () => void;
  onClearAll: () => Promise<void>;
  onCheckout: () => Promise<void>;
  onShowMoreInvalidChange: (show: boolean) => void;
  onMoveToWish: () => void;
  onBatchDelete: () => void;

  // 埋点回调
  onCartPageBannerClick?: () => void;
  onCartPageBannerExp?: () => void;

  // 促销相关
  getItemPromotion: (cartId: number) => any;

  // 其他回调
  onInvalidSelectChange: (index: number, checked: boolean) => void;

  // Swiper管理器
  swiperManager: any;
}

export function CartPageWAP({
  cartCanBuyList,
  cartCanNotBuyList,
  allCheck,
  batchFlag,
  batchDisabled,
  checkoutLoading,
  showMoreInvalid,
  totalData,
  allQty,
  selectedCanBuy,
  currency,
  halfPriceConfig,
  maxSaving,
  showAdsImg,
  advertObj,
  advertData,
  recommendList,
  freeShipData,
  onGoBack,
  onCheckChange,
  onBatchFlagChange,
  onSelectChange,
  onReloadCart,
  onBatchCancel,
  onClearAll,
  onCheckout,
  onShowMoreInvalidChange,
  onMoveToWish,
  onBatchDelete,
  onCartPageBannerClick,
  onCartPageBannerExp,
  getItemPromotion,
  onInvalidSelectChange,
  swiperManager,
}: CartPageWAPProps) {
  const t = useTranslations('cart.cartPage');
  // 广告位曝光埋点
  const [bannerRef, bannerInView] = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  // 处理广告位曝光埋点
  useEffect(() => {
    if (bannerInView && onCartPageBannerExp && showAdsImg && advertObj) {
      try {
        onCartPageBannerExp();
      } catch {
        // 不处理
      }
    }
  }, [bannerInView, onCartPageBannerExp, showAdsImg, advertObj]);

  // 处理广告位点击埋点
  const handleBannerClick = useCallback(() => {
    if (onCartPageBannerClick) {
      setSession('zl_goodsListEnterPageScene', '购物车页广告位');
      try {
        onCartPageBannerClick();
      } catch {
        // 不处理
      }
    }
  }, [onCartPageBannerClick]);

  return (
    <>
      {/* 移动端标题栏 */}
      <div className="sticky top-0 z-[10] bg-white ">
        <div className="flex items-center justify-between px-[14px] h-[45px]">
          <LeftArrowOutlined className="text-[20px]" onClick={onGoBack} />
          <h1 className="font-bs absolute left-1/2 -translate-x-1/2 text-[16px]">
            {t('shoppingCart')}
            {allQty > 0 && <span className="ml-2">({allQty})</span>}
          </h1>
          <div className="flex items-center">
            {(cartCanBuyList.length > 0 || cartCanNotBuyList.length > 0) && (
              <span
                onClick={() => (batchFlag ? onBatchCancel() : onBatchFlagChange(true))}
                className="text-[14px] font-br text-primary-black"
              >
                {batchFlag ? t('done') : t('edit')}
              </span>
            )}
          </div>
        </div>

        {/* 移动端NoticeBar */}
        <NoticeBar
          freeShipData={freeShipData}
          totalData={totalData}
          batchFlag={batchFlag}
          recommendList={recommendList}
          deviceType={EPlatform.WAP}
        />
      </div>

      {/* 移动端主要内容 */}
      <div className="w-full bg-[#f5f6f7]">
        {/* WAP端广告位 */}
        {showAdsImg && (
          <div className="px-[8px] pt-[10px]" ref={bannerRef}>
            <MagicJump item={advertObj} onBeforeJump={handleBannerClick}>
              <Image
                src={advertData.cart_settlement_wap_image}
                alt="Advertisement"
                width={375}
                height={47}
                className="w-full h-auto block mx-auto"
              />
            </MagicJump>
          </div>
        )}

        {/* 可购买商品列表 */}
        <div className="px-[8px] pt-[10px]">
          {cartCanBuyList.map((item, index) => (
            <div key={index} className="pb-[10px]">
              <div className="rounded-[10px] bg-white">
                <div className="">
                  {item.carts?.map((cartItem) => (
                    <CartItem
                      key={cartItem.cart_id}
                      cartItemData={cartItem}
                      batchFlag={batchFlag}
                      onReload={onReloadCart}
                      selected={item.selected || false}
                      itemDisabled={item.disabled || false}
                      onSelectChange={(checked) => onSelectChange(index, checked)}
                      itemType={item.type || 1}
                      itemPromotion={getItemPromotion(cartItem.cart_id || 0)}
                      currency={currency}
                      giftCountDown={item.count_down || 0}
                      giftAllowType={item.is_allow_settlement || 0}
                      itemGiftInfo={item.relationable || {}}
                      swiperManager={swiperManager}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {cartCanBuyList.length === 0 && cartCanNotBuyList.length > 0 && (
          <CartEmpty deviceType={EPlatform.WAP} />
        )}

        {/* 无效商品区域 */}
        {cartCanNotBuyList.length > 0 && (
          <div className="px-[8px] pb-[10px] font-br ">
            <div className="bg-white rounded-[10px] overflow-hidden py-[16px] px-[12px]">
              <div className="flex items-center justify-between">
                <p className="text-[14px]">
                  {cartCanNotBuyList.length > 1
                    ? t('outOfStockInvalidItemsPlural')
                    : t('outOfStockInvalidItems')}
                  ({cartCanNotBuyList.length})
                </p>
                <div className="flex items-center space-x-2">
                  {!batchFlag && showMoreInvalid && (
                    <Button
                      onClick={onClearAll}
                      sx={{
                        padding: '0',
                        fontSize: '12px',
                        textDecoration: 'underline',
                      }}
                    >
                      {t('clearAll')}
                    </Button>
                  )}
                  {!showMoreInvalid && (
                    <Button
                      onClick={() => onShowMoreInvalidChange(!showMoreInvalid)}
                      className="!P-0"
                      sx={{
                        padding: '0',
                        color: '#282828',
                        fontSize: '12px',
                      }}
                    >
                      {t('viewAll')}
                      <CollapseArrowDownOutlined className="text-[14px] ml-[4px]" />
                    </Button>
                  )}
                </div>
              </div>
              {showMoreInvalid && (
                <div className="bg-white rounded-[10px] overflow-hidden">
                  {cartCanNotBuyList.map((item, index) => (
                    <div
                      key={index}
                      className={`flex items-start ${index === 0 ? 'mt-[16px]' : 'mt-[20px]'}`}
                    >
                      <div className="flex-1">
                        <InvalidCartItem
                          cartItemData={item}
                          batchFlag={batchFlag}
                          onReload={onReloadCart}
                          itemIndex={index}
                          currency={currency}
                          selected={item.selected || false}
                          onInvalidSelectChange={(checked) => onInvalidSelectChange(index, checked)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 移动端底部导航栏 */}
      <CartBottomBar
        selectedItems={selectedCanBuy}
        totalData={totalData}
        allCheck={allCheck}
        onAllCheckChange={onCheckChange}
        checkoutLoading={checkoutLoading}
        onCheckout={onCheckout}
        batchFlag={batchFlag}
        batchDisabled={batchDisabled}
        onMoveToWish={onMoveToWish}
        onBatchDelete={onBatchDelete}
        show={cartCanBuyList.length > 0 || cartCanNotBuyList.length > 0}
        maxSaving={maxSaving}
        halfPriceData={halfPriceConfig}
      />
    </>
  );
}
